import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Download,
  Loader2,
  Mail,
  PenLine,
  Sparkles,
} from 'lucide-react';
import { ContractDocumentView } from '@/components/contracts/ContractDocumentView';
import { ContractDecisionsPanel } from '@/components/contracts/ContractDecisionsPanel';
import { ContractSignConfirm } from '@/components/contracts/ContractSignConfirm';
import { ContractClientSignSteps } from '@/components/contracts/ContractClientSignSteps';
import {
  capturedInitialsFromInput,
  clausesFromSource,
  type ContractClause,
} from '@/lib/contract-clauses';
import MainLayout from '@/layouts/MainLayout';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQuery } from '@tanstack/react-query';
import { settingsService } from '@/services/settings';
import {
  DEFAULT_FINANCING_PROGRAMS,
  cashTotalFromSummary,
  decisionBlockMessage,
  decisionsComplete,
  decisionsFromContract,
  findProgram,
  remainingDecisionKeys,
} from '@/lib/contract-decisions';
import { asArray } from '@/lib/as-array';
import {
  applyOverviewToDocumentProps,
  assumptionsFromContract,
  collectProjectDetailRows,
  discountsFromContract,
  formatAddress as joinAddress,
  formatJobsiteAddress,
  investmentDetailsFromContract,
  jobsiteFromUnknown,
  normalizePaymentItems,
  overviewFromContractData,
  overviewItemsFromSummary,
  paymentDetailsSource,
  paymentInstructionsFromContract,
  resolveProjectDocumentId,
} from '@/lib/contract-document-props';
import {
  collectClientParties,
  formatSignatureDate,
  isSigningNameGuidance,
  nextUnsignedClientIndex,
  signatureForParty,
  signingNameInputValue,
  signingTermsFromHtml,
  viewSignatureSlots,
  viewSlotsHaveInk,
} from '@/lib/contract-signing';
import { ContractSignaturePad } from '@/components/contracts/ContractSignaturePad';
import { ContractSignaturePreview } from '@/components/contracts/ContractSignaturePreview';
import { buildCsfBlocks, elementScopeBlocks } from '@/lib/contract-csf';
import {
  fetchContractPdfBlob,
  useContractDetails,
  useGenerateContractNarrative,
  useGenerateContractPDF,
  useMarkContractLost,
  useMarkContractWon,
  useSendContractEmail,
  useSignContract,
  useUpdateContractLegal,
  useUpdateContractDecisions,
  type ContractDetails,
} from '@/hooks/use-contracts';
import { useElectronicSignatureConfig } from '@/hooks/use-electronic-signature';
import { useToast } from '@/hooks/use-toast';
import { authUtils } from '@/utils/auth';
import { PERMISSIONS } from '@/constants/permissions';
import { ROLES } from '@/constants/roles';
import { canCompanyCountersign } from '@/lib/authorized-signers';
import {
  areContractDecisionsLocked,
  canMarkContractOutcome,
  companyCountersignReady,
  isWonContractStatus,
} from '@/lib/contract-status';
import { ContractWonCelebration } from '@/components/contracts/ContractWonCelebration';

const EXECUTED_STATUSES = new Set(['WON', 'EXECUTED', 'EXECUTED_PAPER']);
const EMAILABLE_STATUSES = new Set([
  'DRAFT',
  'SENT',
  'PRESENTED',
  'PENDING_SIGNATURE',
  'PARTIALLY_SIGNED',
  'CLIENT_SIGNED',
]);

function statusBadge(status: string) {
  switch (status) {
    case 'DRAFT':
      return <Badge variant="secondary">Draft</Badge>;
    case 'SENT':
    case 'PRESENTED':
    case 'PENDING_SIGNATURE':
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Sent</Badge>;
    case 'PARTIALLY_SIGNED':
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Partially signed</Badge>;
    case 'CLIENT_SIGNED':
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Awaiting countersign</Badge>;
    case 'EXECUTED':
    case 'EXECUTED_PAPER':
    case 'WON':
      return <Badge className="bg-amber-100 text-amber-900 border-amber-300">Won</Badge>;
    case 'LOST':
    case 'DECLINED':
      return <Badge variant="destructive">{status === 'DECLINED' ? 'Declined' : 'Lost'}</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

function legalHtml(contract: ContractDetails, field: 'contractLanguage' | 'limitedLiabilityWarranty') {
  if (field === 'contractLanguage') {
    return (
      contract.contractLanguage ||
      contract.contractData?.legal?.contractLanguage ||
      contract.contractData?.proposal?.contractLanguage ||
      ''
    );
  }
  return (
    contract.limitedLiabilityWarranty ||
    contract.contractData?.legal?.limitedLiabilityWarranty ||
    contract.contractData?.proposal?.limitedLiabilityWarranty ||
    (typeof contract.contractData?.warranty === 'string' ? contract.contractData.warranty : '') ||
    ''
  );
}

function clientDisplayName(contract: ContractDetails): string {
  const frozen = contract.contractData?.client;
  const related = contract.project?.client;
  return (
    frozen?.fullName ||
    frozen?.businessName ||
    related?.businessName ||
    [related?.firstName, related?.lastName].filter(Boolean).join(' ').trim() ||
    'Client'
  );
}

function clientEmailAddress(contract: ContractDetails): string {
  return (
    contract.project?.client?.primaryEmail ||
    contract.contractData?.client?.email ||
    ''
  );
}

function formatAddress(parts: Array<string | null | undefined>): string | null {
  return joinAddress(parts);
}

function partyHasSigned(
  contract: ContractDetails,
  name: string | null,
  index: number,
  otherPartyName?: string | null,
): boolean {
  if (EXECUTED_STATUSES.has(contract.status) && !name) return false;
  return !!signatureForParty(asArray(contract.clientSignatures), name, index, otherPartyName);
}

function base64ToFile(base64String: string, filename: string): File {
  const byteCharacters = atob(base64String.split(',')[1]);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new File([new Uint8Array(byteNumbers)], filename, { type: 'image/png' });
}

function contractValue(contract: ContractDetails): number | null {
  const summary = contract.contractData?.investmentSummary;
  if (summary?.finalTotal != null) return Number(summary.finalTotal);
  if (summary?.total != null) return Number(summary.total);
  const payments = asArray<{ amount?: number }>(contract.contractData?.proposal?.paymentDetails);
  if (payments.length) return payments.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  return contract.contractData?.project?.investmentMax ?? contract.project?.investmentMax ?? null;
}

function scopeNarrative(contract: ContractDetails): string {
  const scopeField = contract.contractData?.scope;
  return (
    contract.contractData?.project?.summary ||
    contract.contractData?.proposal?.summary ||
    (typeof scopeField === 'string' ? scopeField : '') ||
    contract.project?.summary ||
    ''
  );
}

function toDocumentProps(contract: ContractDetails) {
  const data = contract.contractData ?? {};
  const related: any = contract.project ?? {};
  const client: any = data.client ?? related.client ?? {};
  const org: any = data.organization ?? related.organization ?? {};
  const project: any = data.project ?? related;
  const jobsite =
    jobsiteFromUnknown(project?.jobsiteAddress) ??
    jobsiteFromUnknown(related.jobsiteAddress);
  const payments = normalizePaymentItems(
    paymentDetailsSource(contract),
    contractValue(contract),
  );
  const allCsfBlocks = buildCsfBlocks(data);
  const csfScope = elementScopeBlocks(allCsfBlocks);
  const projectDetailRows = collectProjectDetailRows({
    csfBlocks: allCsfBlocks,
    projectDetails: (data as { projectDetails?: unknown }).projectDetails,
    project: { ...related, ...project },
  });
  const narrative = scopeNarrative(contract);
  const clientParties = collectClientParties(contract);
  const base = {
    contractNumber: resolveProjectDocumentId({
      fieldCode: project?.fieldCode || related.fieldCode,
      projectNumber: project?.projectNumber || related.projectNumber,
      projectDocumentId: project?.projectDocumentId || related.projectDocumentId,
      projectLabel: project?.projectLabel || related.projectLabel,
      contractId: contract.contractId,
    }),
    title: project?.projectLabel || related.projectLabel || 'Construction contract',
    status: contract.status,
    projectName: project?.projectLabel || related.projectLabel || related.projectName,
    projectDetailRows,
    projectAddress: formatJobsiteAddress(jobsite, { ...related, ...project }),
    client: {
      name: clientDisplayName(contract),
      email: clientEmailAddress(contract) || client?.email || client?.primaryEmail,
      phone: client?.phone || client?.primaryContactNumber,
      address: formatAddress([
        client?.address || client?.streetAddress1,
        client?.streetAddress2,
        [client?.city, client?.state, client?.zipCode || client?.postalCode]
          .filter(Boolean)
          .join(', '),
      ]),
      type: client?.clientType || client?.type,
      businessName: client?.businessName || related.client?.businessName,
    },
    company: {
      name: org?.profile?.name || 'Company',
      email: org?.businessEmail,
      phone: org?.profile?.phone,
      website: org?.profile?.website,
      address: formatAddress([
        org?.profile?.address,
        [org?.profile?.city, org?.profile?.state, org?.profile?.zipCode].filter(Boolean).join(', '),
      ]),
    },
    payment: {
      contractValue: contractValue(contract),
      items: payments,
      ...paymentInstructionsFromContract(contract),
    },
    csfScope,
    overviewItems: overviewItemsFromSummary(narrative),
    assumptions: assumptionsFromContract(contract),
    investmentDetails: investmentDetailsFromContract(contract),
    discounts: discountsFromContract(contract),
    contractLanguage: legalHtml(contract, 'contractLanguage'),
    legalClauses: clausesFromSource({
      html: legalHtml(contract, 'contractLanguage'),
      frozen: (data as { legal?: { clauses?: ContractClause[] } }).legal?.clauses,
    }),
    clauseInitials: [
      {
        party: 'Client 1',
        initials: capturedInitialsFromInput(
          signatureForParty(
            asArray(contract.clientSignatures),
            clientParties[0].name,
            0,
            clientParties[1].name,
          )?.clauseInitials,
        ),
      },
      {
        party: 'Client 2',
        initials: capturedInitialsFromInput(
          signatureForParty(
            asArray(contract.clientSignatures),
            clientParties[1].name,
            1,
            clientParties[0].name,
          )?.clauseInitials,
        ),
      },
    ],
    limitedLiabilityWarranty: legalHtml(contract, 'limitedLiabilityWarranty'),
    clientParties,
    contractorSignerName: contract.creator?.fullName || org?.profile?.name,
    signingTerms: signingTermsFromHtml(
      (data as { legal?: { signatureSectionHtml?: string | null } }).legal?.signatureSectionHtml,
      org?.profile?.name,
    ),
  };
  return applyOverviewToDocumentProps(base, overviewFromContractData(contract));
}

export default function ContractPage() {
  const { contractId } = useParams<{ contractId: string }>();
  const { toast } = useToast();

  const { data: contract, isLoading, isError, error, refetch } = useContractDetails(
    contractId || '',
  );
  const generatePdf = useGenerateContractPDF();
  const generateNarrative = useGenerateContractNarrative();
  const sendEmail = useSendContractEmail();
  const signContract = useSignContract();
  const updateDecisions = useUpdateContractDecisions();
  const updateLegal = useUpdateContractLegal();
  const { data: financingCatalog } = useQuery({
    queryKey: ['financing-programs'],
    queryFn: () => settingsService.getFinancingPrograms(),
  });
  const markLost = useMarkContractLost();
  const markWon = useMarkContractWon();
  const { data: esignConfig } = useElectronicSignatureConfig();
  const decisions = decisionsFromContract(contract ?? {});
  const programs = financingCatalog?.financingPrograms?.length
    ? financingCatalog.financingPrograms
    : DEFAULT_FINANCING_PROGRAMS;
  const selectedProgram = findProgram(programs, decisions.financingProgramId);
  const remainingChoices = remainingDecisionKeys(decisions, selectedProgram);
  const choicesComplete = decisionsComplete(decisions, selectedProgram);

  const [clientLink, setClientLink] = useState<string | null>(null);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [client1Name, setClient1Name] = useState('');
  const [client2Name, setClient2Name] = useState('');
  const [client1Signature, setClient1Signature] = useState('');
  const [client2Signature, setClient2Signature] = useState('');
  const [companySignature, setCompanySignature] = useState('');
  const [client1Accepted, setClient1Accepted] = useState(false);
  const [client2Accepted, setClient2Accepted] = useState(false);
  const [companyTerms, setCompanyTerms] = useState(false);
  const [client1Initials, setClient1Initials] = useState<Record<string, string>>({});
  const [client2Initials, setClient2Initials] = useState<Record<string, string>>({});
  const [legalLanguage, setLegalLanguage] = useState('');
  const [legalWarranty, setLegalWarranty] = useState('');
  const [celebrateWon, setCelebrateWon] = useState(false);

  const currentUser = authUtils.getCurrentUser();
  const currentUserId = currentUser?.userId ?? currentUser?.id ?? null;
  const canView = authUtils.hasPermission(PERMISSIONS.CONTRACT.VIEW);
  const canEditLegal = authUtils.hasPermission(PERMISSIONS.CONTRACT.EDIT_LEGAL);
  const canSign = canCompanyCountersign({
    userId: currentUserId,
    isOwner:
      !!currentUser?.isOwner ||
      authUtils.hasRole(ROLES.OWNER.slug) ||
      authUtils.hasRole(ROLES.OWNER.name),
    isAdmin: authUtils.hasRole(ROLES.ADMIN.slug) || authUtils.hasRole(ROLES.ADMIN.name),
    authorizedSignerUserIds: esignConfig?.authorizedSignerUserIds,
    leftoverSign: authUtils.hasPermission('perm-contract-sign'),
    canViewContract: canView,
  });
  const canGenerate = canView;
  const isExecuted =
    !!contract &&
    (contract.status === 'EXECUTED' ||
      contract.status === 'EXECUTED_PAPER' ||
      (contract.status === 'WON' && !!contract.companySignedAt));
  const canEmail = !!contract && EMAILABLE_STATUSES.has(contract.status);
  const clientParties = contract ? collectClientParties(contract) : [{ name: null }, { name: null }];
  const client1Signed =
    !!contract &&
    partyHasSigned(
      contract,
      client1Name || clientParties[0].name,
      0,
      client2Name || clientParties[1].name,
    );
  const namedClient2 = !!(client2Name.trim() || clientParties[1].name);
  const client2Signed =
    !!contract &&
    namedClient2 &&
    partyHasSigned(
      contract,
      client2Name || clientParties[1].name,
      1,
      client1Name || clientParties[0].name,
    );
  const decisionsLocked =
    !!contract &&
    areContractDecisionsLocked(contract.status, client1Signed || client2Signed);
  const client1Sig = contract
    ? signatureForParty(
        asArray(contract.clientSignatures),
        client1Name || clientParties[0].name,
        0,
        client2Name || clientParties[1].name,
      )
    : undefined;
  const client2Sig = contract
    ? signatureForParty(
        asArray(contract.clientSignatures),
        client2Name || clientParties[1].name,
        1,
        client1Name || clientParties[0].name,
      )
    : undefined;
  const requiredClientsSigned = companyCountersignReady(
    contract?.status ?? '',
    client1Signed && (!namedClient2 || client2Signed),
  );
  const nextClientIndex = contract
    ? nextUnsignedClientIndex(
        [clientParties[0], clientParties[1]],
        asArray(contract.clientSignatures),
      )
    : 0;
  const storedSignatureSlots = contract
    ? viewSignatureSlots(contract, {
        contractorName:
          contract.creator?.fullName ||
          contract.companySigner?.fullName ||
          contract.contractData?.organization?.profile?.name ||
          null,
      })
    : [];
  const showStoredInk = viewSlotsHaveInk(storedSignatureSlots);
  const isWonChip = !!contract && isWonContractStatus(contract.status);
  const showSignatureStrip = showStoredInk || isWonChip;

  useEffect(() => {
    const hash = window.location.hash;
    if (hash !== '#contract-signatures' && hash !== '#signatures') return;
    const scrollToInk = () => {
      document.getElementById('contract-signatures')?.scrollIntoView({
        block: 'start',
        behavior: 'auto',
      });
    };
    const frame = window.requestAnimationFrame(scrollToInk);
    const timer = window.setTimeout(scrollToInk, 80);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [showSignatureStrip, contract?.id]);

  useEffect(() => {
    if (!contract) return;
    const [first, second] = collectClientParties(contract);
    if (!client1Name && first.name && !isSigningNameGuidance(first.name)) {
      setClient1Name(first.name);
    }
    if (!client2Name && second.name && !isSigningNameGuidance(second.name)) {
      setClient2Name(second.name);
    }
  }, [client1Name, client2Name, contract]);

  useEffect(() => {
    if (!contract) return;
    setLegalLanguage(legalHtml(contract, 'contractLanguage'));
    setLegalWarranty(legalHtml(contract, 'limitedLiabilityWarranty'));
  }, [contract]);

  const onDownload = useCallback(async () => {
    if (!contractId) return;
    try {
      if (!contract?.contractPdfUrl && canGenerate) {
        await generatePdf.mutateAsync({ contractId });
        await refetch();
      }
      const blob = await fetchContractPdfBlob(contractId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${contract?.contractId || 'contract'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: 'Download failed',
        description: err instanceof Error ? err.message : 'Could not download the contract PDF.',
        variant: 'destructive',
      });
    }
  }, [canGenerate, contract?.contractId, contract?.contractPdfUrl, contractId, generatePdf, refetch, toast]);

  const onGenerateNarrative = useCallback(async () => {
    if (!contractId) return;
    try {
      const result = await generateNarrative.mutateAsync(contractId);
      await refetch();
      toast({
        title: 'Narrative generated',
        description:
          result.summary?.slice(0, 180) ||
          'The AI overview was saved on this contract.',
      });
    } catch {
      // useGenerateContractNarrative already toasts the API/config message.
    }
  }, [contractId, generateNarrative, refetch, toast]);

  const onEmail = async () => {
    if (!contractId || !contract) return;
    if (contract.status === 'DRAFT' && !choicesComplete) {
      toast({
        title: 'Decisions still open',
        description: decisionBlockMessage(remainingChoices),
        variant: 'destructive',
      });
      return;
    }
    const awaitingCountersign =
      !contract.companySignedAt &&
      (contract.status === 'CLIENT_SIGNED' || contract.status === 'WON');
    const recipient = clientEmailAddress(contract);
    const myEmail = authUtils.getCurrentUser()?.email;
    if (
      !window.confirm(
        awaitingCountersign
          ? myEmail
            ? `Send the countersign notice to ${myEmail}? You can also countersign on this page now — you do not need the email.`
            : 'Send the countersign notice to your login email? You can also countersign on this page now — you do not need the email.'
          : recipient
            ? `Send the signing email to ${recipient}? Client 1 signs first; client 2 is emailed after that; you countersign last.`
            : 'Send the signing email to the next unsigned client? Client 1 signs first; client 2 is emailed after that; you countersign last.',
      )
    ) {
      return;
    }
    const result = await sendEmail.mutateAsync({
      contractId: contract.id,
      isReminder: contract.status !== 'DRAFT',
    });
    if (result.contractLink) {
      setClientLink(result.contractLink);
      try {
        await navigator.clipboard.writeText(result.contractLink);
      } catch {
        /* link stays on the page */
      }
    }
    toast({
      title: awaitingCountersign ? 'Contractor notified' : 'Remote signing started',
      description: awaitingCountersign
        ? result.sentTo
          ? `Countersign notice sent to ${result.sentTo}.`
          : 'The contractor was notified to countersign.'
        : result.sentTo
          ? `Sent to ${result.sentTo}. The client signs first; you countersign after.`
          : 'The client will sign first. You will be notified to countersign.',
    });
    await refetch();
  };

  const onCopyLink = async () => {
    if (!clientLink) return;
    await navigator.clipboard.writeText(clientLink);
    toast({ title: 'Copied', description: 'Client signing link copied.' });
  };

  const legalClauses = clausesFromSource({
    html: contract ? legalHtml(contract, 'contractLanguage') : '',
    frozen: (contract?.contractData as { legal?: { clauses?: ContractClause[] } } | undefined)
      ?.legal?.clauses,
  });

  const initialsPayload = (initials: Record<string, string>) =>
    Object.entries(initials)
      .filter(([, text]) => text.trim())
      .map(([clauseId, text]) => ({ clauseId, text: text.trim() }));

  const onSignParty = async (party: 'client-1' | 'client-2' | 'company') => {
    if (!contractId || !contract) return;
    if ((party === 'client-1' || party === 'client-2') && !choicesComplete) {
      toast({
        title: 'Decisions still open',
        description: decisionBlockMessage(remainingChoices),
        variant: 'destructive',
      });
      return;
    }
    const currentUser = authUtils.getCurrentUser();
    const userId = currentUser?.userId ?? currentUser?.id;
    const signatures: File[] = [];
    const signatureData: Array<{
      signerType: 'CLIENT' | 'COMPANY';
      agreementToTerms: boolean;
      esignConsent?: boolean;
      clientName?: string;
      userId?: string;
      clauseInitials?: Array<{ clauseId: string; text: string }>;
    }> = [];

    if (party === 'client-1') {
      const name = signingNameInputValue(client1Name);
      if (!client1Signature || !name || !client1Accepted) return;
      signatures.push(base64ToFile(client1Signature, 'client-1-signature.png'));
      signatureData.push({
        signerType: 'CLIENT',
        agreementToTerms: true,
        esignConsent: true,
        clientName: name,
        clauseInitials: initialsPayload(client1Initials),
      });
    } else if (party === 'client-2') {
      const name = signingNameInputValue(client2Name);
      if (!client2Signature || !name || !client2Accepted) return;
      signatures.push(base64ToFile(client2Signature, 'client-2-signature.png'));
      signatureData.push({
        signerType: 'CLIENT',
        agreementToTerms: true,
        esignConsent: true,
        clientName: name,
        clauseInitials: initialsPayload(client2Initials),
      });
    } else {
      if (!companySignature || !companyTerms || !userId) return;
      signatures.push(base64ToFile(companySignature, 'company-signature.png'));
      signatureData.push({
        signerType: 'COMPANY',
        agreementToTerms: true,
        userId,
      });
    }

    await signContract.mutateAsync({ contractId, signatures, signatureData });
    if (party === 'client-1') setClient1Signature('');
    if (party === 'client-2') setClient2Signature('');
    if (party === 'company') setCompanySignature('');
    await refetch();
    if (party === 'company') setCelebrateWon(true);
  };

  const subtitle = useMemo(() => {
    if (!contract) return undefined;
    const client = clientDisplayName(contract);
    const project =
      contract.contractData?.project?.projectLabel ||
      contract.project?.projectLabel ||
      contract.project?.projectName;
    return [contract.contractId, client, project].filter(Boolean).join(' · ');
  }, [contract]);

  if (!contractId) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <AlertTriangle className="h-10 w-10 text-destructive mb-3" />
          <h1 className="text-xl font-semibold">Invalid contract</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Open a contract from the Contracts list or a finalized estimate.
          </p>
        </div>
      </MainLayout>
    );
  }

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20 gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading contract…
        </div>
      </MainLayout>
    );
  }

  if (isError || !contract) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <AlertTriangle className="h-10 w-10 text-destructive mb-3" />
          <h1 className="text-xl font-semibold">Failed to load contract</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {error instanceof Error ? error.message : 'The contract could not be loaded.'}
          </p>
        </div>
      </MainLayout>
    );
  }

  const recipientEmail = clientEmailAddress(contract);
  const companyPadEnabled = requiredClientsSigned;
  const cashTotal = cashTotalFromSummary(
    contract.contractData?.investmentSummary,
    contractValue(contract) ?? 0,
  );
  const clientSignReady = choicesComplete;
  const emailBlocked = canEmail && contract.status === 'DRAFT' && !choicesComplete;

  return (
    <MainLayout>
      <div className="space-y-6 pb-12">
        <PageHeader
          title="Contract"
          subtitle={subtitle}
          backTo="/contracts"
          backLabel="Back to contracts"
          actions={
            <div className="flex flex-wrap items-center justify-end gap-2">
              {statusBadge(contract.status)}
              {canView && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="contract-generate-narrative"
                  onClick={() => void onGenerateNarrative()}
                  disabled={generateNarrative.isPending}
                >
                  {generateNarrative.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-1.5" />
                  )}
                  Generate with AI
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => void onDownload()}
                disabled={generatePdf.isPending}
              >
                {generatePdf.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : (
                  <Download className="h-4 w-4 mr-1.5" />
                )}
                Download
              </Button>
              {canEmail && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void onEmail()}
                  disabled={sendEmail.isPending || emailBlocked}
                  title={emailBlocked ? decisionBlockMessage(remainingChoices) : undefined}
                >
                  {sendEmail.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : (
                    <Mail className="h-4 w-4 mr-1.5" />
                  )}
                  Email
                </Button>
              )}
              {canMarkContractOutcome(contract.status) && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="action-mark-lost"
                    disabled={markLost.isPending || markWon.isPending}
                    onClick={() => {
                      if (!window.confirm('Mark this contract lost? It will leave the Active list.')) return;
                      void markLost.mutateAsync(contract.id).then(() => refetch());
                    }}
                  >
                    {markLost.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                    Mark lost
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="action-mark-won"
                    disabled={markLost.isPending || markWon.isPending}
                    onClick={() => {
                      if (!window.confirm('Mark this contract won?')) return;
                      void markWon.mutateAsync(contract.id).then(() => {
                        setCelebrateWon(true);
                        return refetch();
                      });
                    }}
                  >
                    {markWon.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                    Mark won
                  </Button>
                </>
              )}
            </div>
          }
        />

        {showSignatureStrip && (
          <div className="cd-paper p-4" id="contract-signatures" data-testid="contract-view-signatures">
            <p className="cd-sub-h">Signatures</p>
            <ContractSignaturePreview slots={storedSignatureSlots} />
          </div>
        )}

        {(contract.status === 'CLIENT_SIGNED' || contract.status === 'WON') &&
          !contract.companySignedAt && (
          <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/30">
            <PenLine className="h-5 w-5 text-amber-700 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                Both clients have signed
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Countersign below to execute the sale — you do not need the email. Email sends that notice to your login inbox and any org owner with a real address.
              </p>
            </div>
          </div>
        )}

        {isExecuted && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/30">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-amber-600 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  This sale is won
                </p>
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  The contract and the sales status are won. This is the locked document of record.
                </p>
              </div>
            </div>
            {contract.projectId && (
              <Button asChild size="sm" variant="outline">
                <Link to={`/projects/${contract.projectId}/budget`}>Open the project budget</Link>
              </Button>
            )}
          </div>
        )}

        {clientLink && (
          <Card>
            <CardContent className="pt-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <p className="text-sm text-muted-foreground flex-1 break-all">
                Client signing link: {clientLink}
              </p>
              <Button size="sm" variant="outline" onClick={() => void onCopyLink()}>
                <Copy className="h-4 w-4 mr-1.5" />
                Copy link
              </Button>
            </CardContent>
          </Card>
        )}

        {canEmail && recipientEmail && (
          <p className="text-xs text-muted-foreground">
            Email sends to {recipientEmail}. Download saves a print-ready PDF.
          </p>
        )}

        <ContractDocumentView
          {...toDocumentProps(contract)}
          activeInitialsCapture={
            !isExecuted &&
            canSign &&
            clientSignReady &&
            !companyPadEnabled &&
            nextClientIndex === 0 &&
            !client1Sig
              ? {
                  partyKey: 'client-1',
                  partyLabel: client1Name || 'Client 1',
                  initials: client1Initials,
                  onInitialChange: (id, text) =>
                    setClient1Initials((prev) => ({ ...prev, [id]: text })),
                }
              : !isExecuted &&
                  canSign &&
                  clientSignReady &&
                  !companyPadEnabled &&
                  namedClient2 &&
                  nextClientIndex === 1 &&
                  !client2Sig
                ? {
                    partyKey: 'client-2',
                    partyLabel: client2Name || 'Client 2',
                    initials: client2Initials,
                    onInitialChange: (id, text) =>
                      setClient2Initials((prev) => ({ ...prev, [id]: text })),
                  }
                : null
          }
          languageToolbar={
            canEditLegal ? (
              <div className="cd-lang-toolbar" data-testid="contract-language-toolbar">
                <span>
                  Language from Settings for the office serving this client. Edit revises this
                  contract only.
                </span>
                {!isExecuted ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="contract-edit-language"
                    onClick={() => setLanguageOpen(true)}
                  >
                    Edit
                  </Button>
                ) : null}
              </div>
            ) : (
              <p className="cd-lang-toolbar">
                Language from Settings for the office serving this client.
              </p>
            )
          }
          investmentExtras={
            <ContractDecisionsPanel
              section="choice"
              cashTotal={cashTotal}
              programs={programs}
              decisions={decisions}
              disabled={decisionsLocked}
              pending={updateDecisions.isPending}
              onSave={async (patch) => {
                if (!contractId || decisionsLocked) return;
                await updateDecisions.mutateAsync({ contractId, ...patch });
                await refetch();
              }}
            />
          }
          investmentAfterCalc={
            !decisionsLocked ? (
              <ContractDecisionsPanel
                section="downpay"
                cashTotal={cashTotal}
                programs={programs}
                decisions={decisions}
                pending={updateDecisions.isPending}
                onSave={async (patch) => {
                  if (!contractId || decisionsLocked) return;
                  await updateDecisions.mutateAsync({ contractId, ...patch });
                  await refetch();
                }}
              />
            ) : null
          }
          hideMilestones={!decisions.financingProgramId}
          signBlockedNote={
            !isExecuted && !clientSignReady ? (
              <p className="cd-sign-blocked" data-testid="contract-sign-blocked">
                {decisionBlockMessage(remainingChoices)} Signature unlocks after financing, any
                required application, and down payment are on this contract.
              </p>
            ) : null
          }
          signatureSlots={[
            {
              key: 'client-1',
              role: 'Client 1',
              name: client1Name || clientParties[0].name,
              signedAt:
                formatSignatureDate(client1Sig?.signedAt) ||
                (client1Signed ? 'Signed' : null),
              signatureImage:
                client1Sig?.signatureUrl ||
                storedSignatureSlots.find((slot) => slot.key === 'client-1')?.signatureImage ||
                (client1Sig ? client1Signature : null),
              waitNote:
                !isExecuted &&
                canSign &&
                !companyPadEnabled &&
                client1Signed &&
                namedClient2 &&
                !client2Signed
                  ? 'Signed. Client 2 signs next, separately.'
                  : null,
              capture:
                !isExecuted &&
                canSign &&
                clientSignReady &&
                !companyPadEnabled &&
                nextClientIndex === 0 &&
                !client1Sig ? (
                  <ContractClientSignSteps
                    partyKey="client-1"
                    partyName={client1Name || 'Client 1'}
                    printedName={signingNameInputValue(client1Name)}
                    onPrintedNameChange={setClient1Name}
                    printedNameLabel="Client 1 signing name"
                    printedNamePlaceholder="Client 1 full name"
                    signature={client1Signature}
                    onSignatureChange={setClient1Signature}
                    clauses={legalClauses}
                    initials={client1Initials}
                    onInitialChange={(id, text) =>
                      setClient1Initials((prev) => ({ ...prev, [id]: text }))
                    }
                    accepted={client1Accepted}
                    onAccepted={setClient1Accepted}
                    pending={signContract.isPending}
                    onSubmit={() => void onSignParty('client-1')}
                  />
                ) : undefined,
            },
            {
              key: 'client-2',
              role: 'Client 2',
              name: client2Name || clientParties[1].name,
              signedAt:
                formatSignatureDate(client2Sig?.signedAt) ||
                (client2Signed ? 'Signed' : null),
              signatureImage:
                client2Sig?.signatureUrl ||
                storedSignatureSlots.find((slot) => slot.key === 'client-2')?.signatureImage ||
                (client2Sig ? client2Signature : null),
              waitNote:
                !isExecuted &&
                canSign &&
                !companyPadEnabled &&
                namedClient2 &&
                nextClientIndex === 0 &&
                !client2Sig
                  ? 'Client 2 signs after Client 1, separately.'
                  : null,
              capture:
                !isExecuted &&
                canSign &&
                clientSignReady &&
                !companyPadEnabled &&
                namedClient2 &&
                nextClientIndex === 1 &&
                !client2Sig ? (
                  <ContractClientSignSteps
                    partyKey="client-2"
                    partyName={client2Name || 'Client 2'}
                    printedName={signingNameInputValue(client2Name)}
                    onPrintedNameChange={setClient2Name}
                    printedNameLabel="Client 2 signing name"
                    printedNamePlaceholder="Client 2 full name"
                    signature={client2Signature}
                    onSignatureChange={setClient2Signature}
                    clauses={legalClauses}
                    initials={client2Initials}
                    onInitialChange={(id, text) =>
                      setClient2Initials((prev) => ({ ...prev, [id]: text }))
                    }
                    accepted={client2Accepted}
                    onAccepted={setClient2Accepted}
                    pending={signContract.isPending}
                    onSubmit={() => void onSignParty('client-2')}
                  />
                ) : undefined,
            },
            ...storedSignatureSlots
              .filter((slot) => slot.key.startsWith('client-extra-'))
              .map((slot) => ({
                key: slot.key,
                role: slot.role,
                name: slot.name,
                signedAt: slot.signedAt,
                signatureImage: slot.signatureImage,
              })),
            {
              key: 'contractor',
              role: 'Contractor',
              name: contract.creator?.fullName || contract.contractData?.organization?.profile?.name,
              signedAt: formatSignatureDate(contract.companySignedAt),
              signatureImage: contract.companySignature,
              waitNote:
                !contract.companySignature && isWonChip
                  ? 'Company has not countersigned yet.'
                  : !isExecuted && canSign && !contract.companySignedAt && !companyPadEnabled
                    ? 'Signs after both clients.'
                    : !isExecuted && !canSign && !contract.companySignedAt
                      ? 'Only the Owner or an authorized company signer may countersign.'
                      : null,
              capture:
                !isExecuted && canSign && !contract.companySignedAt && companyPadEnabled ? (
                  <ContractSignaturePad
                    partyName={contract.creator?.fullName || 'Contractor'}
                    value={companySignature}
                    onChange={setCompanySignature}
                  />
                ) : undefined,
            },
          ]}
        >
          {!isExecuted && (canView || canSign) && (
            <div data-testid="in-person-signing" className="cd-sign-confirm">
              <p className="cd-sig-note">
                Each client signs separately and in order. Client 1 finishes first; Client 2 then consents and signs. The contractor signs after both clients.
                {!clientSignReady
                  ? ' Signature stays locked until financing and down payment are complete.'
                  : ''}
              </p>
              {companyPadEnabled && !contract.companySignedAt ? (
                <ContractSignConfirm
                  testId="company-sign-confirm"
                  includeDisclosure={false}
                  checkboxId="company-agree"
                  accepted={companyTerms}
                  onAccepted={setCompanyTerms}
                  label="I confirm the company countersignature after both clients."
                  primaryLabel="Confirm countersign"
                  primaryDisabled={!companySignature || !companyTerms}
                  primaryPending={signContract.isPending}
                  onPrimary={() => void onSignParty('company')}
                />
              ) : null}
            </div>
          )}
        </ContractDocumentView>
        <Dialog
          open={languageOpen}
          onOpenChange={(open) => {
            if (!open) {
              setLegalLanguage(legalHtml(contract, 'contractLanguage'));
              setLegalWarranty(legalHtml(contract, 'limitedLiabilityWarranty'));
            }
            setLanguageOpen(open);
          }}
        >
          <DialogContent data-testid="contract-edit-legal" className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit language on this contract</DialogTitle>
              <DialogDescription>
                Changes apply to this contract only. Company language packs stay in Settings.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="contract-language">Contract language</Label>
                <Textarea
                  id="contract-language"
                  value={legalLanguage}
                  onChange={(event) => setLegalLanguage(event.target.value)}
                  disabled={isExecuted || updateLegal.isPending}
                  className="min-h-[140px] font-mono text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contract-warranty">Limited liability / warranty</Label>
                <Textarea
                  id="contract-warranty"
                  value={legalWarranty}
                  onChange={(event) => setLegalWarranty(event.target.value)}
                  disabled={isExecuted || updateLegal.isPending}
                  className="min-h-[140px] font-mono text-sm"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setLegalLanguage(legalHtml(contract, 'contractLanguage'));
                  setLegalWarranty(legalHtml(contract, 'limitedLiabilityWarranty'));
                  setLanguageOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={isExecuted || updateLegal.isPending || !contractId}
                onClick={() => {
                  if (!contractId) return;
                  void updateLegal
                    .mutateAsync({
                      contractId,
                      contractLanguage: legalLanguage,
                      limitedLiabilityWarranty: legalWarranty,
                    })
                    .then(async () => {
                      setLanguageOpen(false);
                      await refetch();
                      if (canGenerate) {
                        await generatePdf.mutateAsync({ contractId });
                        await refetch();
                      }
                    });
                }}
              >
                {updateLegal.isPending || generatePdf.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <ContractWonCelebration
        open={celebrateWon}
        onOpenChange={setCelebrateWon}
        clientName={contract ? clientDisplayName(contract) : null}
        projectId={contract?.projectId}
      />
    </MainLayout>
  );
}
